/**
 * MoveHistoryNavigator Component
 * 
 * Provides UI controls for navigating through a sequence of chess moves.
 */

import React from 'react';
import styles from './MoveHistoryNavigator.module.css';
import Tooltip from '../../common/Tooltip/Tooltip';
import NotationService from '../../../services/chess/notationService';

export interface MoveHistoryNavigatorProps {
  /** Array of moves in algebraic notation */
  moveHistory: string[];
  /** Current position in the move history */
  currentMoveIndex: number;
  /** Callback for when a move is selected */
  onNavigateToMove: (index: number) => void;
  /** Optional class name for custom styling */
  className?: string;
}

/**
 * MoveHistoryNavigator - A component for navigating through chess move history
 * 
 * Provides controls to step through moves, jump to specific positions,
 * and display the current position in the sequence.
 */
const MoveHistoryNavigator: React.FC<MoveHistoryNavigatorProps> = ({
  moveHistory,
  currentMoveIndex,
  onNavigateToMove,
  className,
}) => {
  // No need to render if there's no history or navigation function
  if (!moveHistory || moveHistory.length === 0 || !onNavigateToMove) return null;

  // Instantiate NotationService
  const notationService = new NotationService();

  // Calculate the total number of "positions" (initial + after each move)
  const totalPositions = moveHistory.length + 1;
  
  // Handle navigation button clicks
  const handleFirst = () => onNavigateToMove(0);
  const handlePrevious = () => onNavigateToMove(Math.max(0, currentMoveIndex - 1));
  const handleNext = () => onNavigateToMove(Math.min(moveHistory.length, currentMoveIndex + 1));
  const handleLast = () => onNavigateToMove(moveHistory.length);
  
  // Determine if buttons should be disabled
  const isFirst = currentMoveIndex === 0;
  const isLast = currentMoveIndex === moveHistory.length;

  // Format the current position as "x/y" (e.g., "3/8")
  const positionLabel = `${currentMoveIndex}/${moveHistory.length}`;
  
  // Handle clicking on a specific move
  const handleMoveClick = (index: number) => {
    onNavigateToMove(index + 1); // +1 because moves are 0-indexed but positions are 1-indexed
  };
  
  // Format moves with move numbers (1. e4 e5 2. Nf3 Nc6)
  const renderFormattedMoves = () => {
    const elements: JSX.Element[] = [];
    
    for (let i = 0; i < moveHistory.length; i++) {
      // Add move number for white's moves (even indices)
      if (i % 2 === 0) {
        const moveNumber = Math.floor(i / 2) + 1;
        elements.push(
          <span key={`num-${i}`} className={styles.moveNumber}>
            {moveNumber}.
          </span>
        );
      }
      
      // Add the move itself
      elements.push(
        <Tooltip 
          key={`tooltip-move-${i}`} 
          content={notationService.generateHint(moveHistory[i]) || moveHistory[i]}
          position="top"
        >
          <button
            key={`move-${i}`}
            className={`${styles.moveButton} ${i + 1 === currentMoveIndex ? styles.currentMove : ''}`}
            onClick={() => handleMoveClick(i)}
            aria-current={i + 1 === currentMoveIndex ? 'step' : undefined}
          >
            {moveHistory[i]}
          </button>
        </Tooltip>
      );
    }
    
    return elements;
  };

  return (
    <div className={`${styles.container} ${className || ''}`} data-testid="move-history-navigator">
      <div className={styles.controls}>
        <button 
          className={styles.navButton} 
          onClick={handleFirst} 
          disabled={isFirst}
          aria-label="Go to starting position"
          data-testid="nav-first"
        >
          <span className={styles.navIcon}>⏮</span>
        </button>
        <button 
          className={styles.navButton} 
          onClick={handlePrevious} 
          disabled={isFirst}
          aria-label="Go to previous move"
          data-testid="nav-prev"
        >
          <span className={styles.navIcon}>◀</span>
        </button>
        <span className={styles.positionLabel} title={`Position ${currentMoveIndex} of ${moveHistory.length}`}>
          {positionLabel}
        </span>
        <button 
          className={styles.navButton} 
          onClick={handleNext} 
          disabled={isLast}
          aria-label="Go to next move"
          data-testid="nav-next"
        >
          <span className={styles.navIcon}>▶</span>
        </button>
        <button 
          className={styles.navButton} 
          onClick={handleLast} 
          disabled={isLast}
          aria-label="Go to final position"
          data-testid="nav-last"
        >
          <span className={styles.navIcon}>⏭</span>
        </button>
      </div>
      
      <div className={styles.moveList} aria-label="Move history">
        {renderFormattedMoves()}
      </div>
    </div>
  );
};

export default MoveHistoryNavigator; 